import { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent, imgUrl } from "@/context/ContentContext";
import { api } from "@/lib/api";

const links = [
  { to: "/", label: "Home" },
  { to: "/workshops", label: "Book a Workshop" },
  { to: "/themes", label: "Book by Theme" },
  { to: "/private-events", label: "Private Events" },
  { to: "/corporate", label: "Corporate & Mobile" },
  { to: "/custom-rugs", label: "Custom Rugs" },
  { to: "/tuft-buddies", label: "Tuft Buddies" },
  { to: "/patch-hat-bar", label: "Patch Hat Bar" },
  { to: "/community", label: "Community" },
  { to: "/gift-cards", label: "Gift Cards" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const { site } = useContent();
  const [extra, setExtra] = useState([]);
  useEffect(() => { api.get("/pages").then((r) => setExtra(r.data.map((p) => ({ to: `/p/${p.slug}`, label: p.nav_label || p.title })))).catch(() => {}); }, []);
  const allLinks = [...links, ...extra];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-colors ${scrolled ? "bg-white/90 backdrop-blur-xl border-b border-cream-alt shadow-card" : "bg-white/60 backdrop-blur-md"}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="logo-link">
          <img src={imgUrl(site.logo_image)} alt={site.business_name} className="h-14 md:h-16 w-auto"/>
        </Link>

        <nav className="hidden xl:flex items-center gap-0.5">
          {links.slice(1).map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.replace(/[^a-z]+/gi, '-').toLowerCase()}`}
              className={({ isActive }) => `px-3 py-2 rounded-full text-sm font-semibold transition-colors ${isActive ? "bg-terracotta/10 text-terracotta" : "text-charcoal hover:text-terracotta"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button onClick={() => nav(site.header_button_link || "/workshops")} data-testid="header-book-now-btn"
            className="hidden sm:inline-flex items-center rounded-full bg-terracotta text-white px-5 py-2.5 text-sm font-bold shadow-button transition-transform hover:-translate-y-0.5 hover:bg-terracotta-light">
            {site.header_button_label}
          </button>
          <button onClick={() => setOpen(!open)} className="xl:hidden p-2 rounded-full hover:bg-cream-alt" data-testid="mobile-menu-btn" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-cream-alt" data-testid="mobile-menu">
          <div className="max-w-7xl mx-auto px-6 py-4 grid gap-1">
            {allLinks.map((l) => (
              <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                className={({ isActive }) => `px-4 py-3 rounded-2xl font-semibold ${isActive ? "bg-terracotta/10 text-terracotta" : "text-charcoal"}`}>
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
